import { CopyButton } from "./CopyButton";
import { formatDate } from "../lib/format";
import { formatCount, formatMoney, formatRate } from "../lib/money";
import type { AffiliatePromotion } from "../lib/types";
import { useTranslation } from "react-i18next";

/**
 * One campaign, as a tile.
 *
 * A tile rather than a full-width row, and that is what makes the page compact:
 * an offer is four short lines, and a row gave each of them the width of the
 * screen and stacked them down it. Two to a grid halves the height of the list
 * without shrinking anything a reader has to read.
 *
 * The code leads, because the code is the payload. Everything else on the tile
 * explains a thing the affiliate will say in their own words; the code is the
 * one part that has to be reproduced exactly, and it was first drawn as the
 * smallest element on the card, across a wide row from its own copy button.
 */
export function Offer({ promotion }: { promotion: AffiliatePromotion }) {
  /*
   * What the customer gets, in one line. The two kinds are different offers and
   * must never share a sentence: a match advertised as credit on signup sends
   * somebody to an empty balance.
   */
  const { t } = useTranslation("offer");
  const worth =
    promotion.kind === "grant"
      ? t("grantWorth", { amount: formatMoney(promotion.grantMicros) })
      : t("matchWorth", { rate: formatRate(promotion.matchBps), cap: formatMoney(promotion.matchCapMicros) });

  /*
   * The conditions, as separate facts. Each is something an affiliate is asked
   * and would otherwise have to guess, and a reader scanning a dot-separated
   * row finds the one that applies to them faster than they find a clause in
   * the middle of a paragraph.
   */
  const terms = [
    promotion.kind === "match" && promotion.matchMinTopupMicros
      ? t("needsTopup", { amount: formatMoney(promotion.matchMinTopupMicros) })
      : null,
    promotion.newMembersOnly ? t("newOnly") : t("anyAccount"),
    promotion.creditTtlDays ? t("ttl", { count: promotion.creditTtlDays }) : null,
    promotion.spendableOn === "workloads" ? t("workloadsOnly") : null,
  ].filter(Boolean);

  /*
   * Reasons to share it sooner, and only where there is one. A campaign with no
   * end and no quota gets no line at all rather than "No end date", which is
   * the absence of urgency printed as though it were information.
   *
   * `remaining` is null on an uncapped campaign, which is not the same as none
   * left and must never render as a number.
   */
  const urgency = [
    promotion.remaining !== null ? t("left", { count: formatCount(promotion.remaining) }) : null,
    promotion.endsAt ? t("ends", { date: formatDate(promotion.endsAt) }) : null,
  ].filter(Boolean);

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-line bg-raised/40 p-4">
      <div className="flex flex-wrap items-center gap-2">
        <code className="rounded bg-raised px-2.5 py-1.5 font-mono text-base tracking-wide text-fg">
          {promotion.code}
        </code>
        <CopyButton value={promotion.code} label={t("copyCode")} />
      </div>

      <p className="text-[13px] text-fg">{worth}</p>

      {terms.length > 0 && <p className="text-xs text-fg-muted">{terms.join(" · ")}</p>}

      {/*
        A match is worth saying something about that a grant is not: it pays on
        the top-up, and the top-up is the event the affiliate's own commission
        fires on. A big signup grant does the opposite — a customer sitting on
        free credit does not pay for months — and an affiliate choosing between
        two offers has no way to know that from the terms.
      */}
      {promotion.kind === "match" && (
        <p className="text-xs text-mint">
          {t("matchNote")}
        </p>
      )}

      {/* Pushed to the bottom edge, so tiles of unequal length still line their
          urgency up along one row rather than wherever their copy ended. */}
      {urgency.length > 0 && (
        <p className="mt-auto pt-1 text-xs text-warn">{urgency.join(" · ")}</p>
      )}
    </div>
  );
}
